import * as React from "react";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface NumericInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  step?: string;
  min?: string;
  max?: string;
  placeholder?: string;
}

/**
 * NumericInput manages its own local string state during editing.
 * It only commits to parent state on blur or Enter, preventing
 * the "jumping numbers" issue when typing quickly.
 */
const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  ({ value, onChange, className, step, min, max, placeholder }, ref) => {
    const [localValue, setLocalValue] = useState<string>(String(value));
    const [isFocused, setIsFocused] = useState(false);

    // Sync local value when parent value changes (but not while focused)
    useEffect(() => {
      if (!isFocused) {
        setLocalValue(String(value));
      }
    }, [value, isFocused]);

    const commitValue = () => {
      const parsed = parseFloat(localValue);
      if (!isNaN(parsed)) {
        onChange(parsed);
      } else if (localValue === "" || localValue === "-") {
        // Empty or just a minus sign - commit as 0
        onChange(0);
        setLocalValue("0");
      } else {
        // Invalid input - reset to parent value
        setLocalValue(String(value));
      }
    };

    return (
      <Input
        ref={ref}
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          commitValue();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commitValue();
            e.currentTarget.blur();
          }
        }}
        className={cn(className)}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
      />
    );
  }
);

NumericInput.displayName = "NumericInput";

export { NumericInput };
